import {ChangeDetectorRef, Component, Input, Renderer2} from "@angular/core";
import {EventBus} from "../event/EventBus";
import {StepService} from "../services/step/step.service";
import {PreviewStep, ReadAndRecordStep, QuizStep} from "../services/step/step.objects";
import {SelectionLoadedEvent} from "../event/ContentEvents";
import {
    StepStatusEvent,
    ReadAndRecordStepEvent,
    QuizStepEvent,
    PreviewStepEvent,
    StepAllowedEvent
} from "../event/StepEvents";
import {CompletionState, QuestionState} from "../services/usermodel/usermodel.objects";
import {
    ReadSelectionEvent,
    PauseSelectionEvent,
    NextSentenceEvent,
    RecordSelectionEvent,
    CountdownStartEvent,
    CountdownStopEvent,
    ReadMyRecordingEvent
} from "../event/ReadAndRecordEvents";
import {MicrophoneCheckEvent, MICROPHONECHECK_STATUS} from "../event/MicrophoneCheckEvent";
import {ComponentBase} from "../base/ComponentBase";
import {SessionService} from "../services/configuration/session.service";
import {QuizService} from "../services/quiz/quiz.service";
import {AssignmentService} from "../services/assignment/assignment.service";
import {QuestionService} from "../services/question/question.service";
import {UserModelService} from "../services/usermodel/usermodel.service";
import {RaEvent} from "../event/RaEvent";
import {StartQuizEvent} from "../event/QuizEvents";
import {IQuestionEntry} from "../api/data-model";
import {StepStateUpdater} from "../services/step/step-state";
import {UserProgress} from "../services/usermodel/user-progress";
import {EnableExitButtonEvent} from "../event/ExitButtonEvents";
import {TaiEvent, TAI_STATUS} from "../event/TaiEvent";
import {AudioQueueService} from "../services/audio-playback/audioqueue.service";
import {AudioQueueEvent, InterruptAudio} from "../event/AudioQueueEvent";
import {AudioQueueItem, TranslateAudio} from "../services/audio-playback/audioqueue.item";
import {ScrollService} from "../services/scroll/scroll.service";
import {GlossaryService} from '../services/glossary/glossary.service';
import {filter} from "rxjs/operators";
import {BookSessionType} from "../constants";

export interface IQuestionClass {
    "regular-progress-icon": boolean;
    "active": boolean;
}

@Component({
    selector: "step-view",
    templateUrl: "./step.component.html",
    styleUrls: ["./step.component.scss"]
})

export class StepComponent extends ComponentBase {

    @Input()
    public previewProgressValue: number = 0;

    @Input()
    public readAndRecordProgressValue: number = 0;

    @Input()
    public quizProgressValue: number = 0;

    @Input()
    public direction: string = "vertical";

    @Input()
    public isShowTai;

    

  @Input()
  public showCountdown:boolean = false;

    public previewStepAllowed: boolean = true;
    public readAndRecordStepAllowed: boolean = false;
    public quizStepAllowed: boolean = false;

    public previewStepActive: boolean = true;
    public readAndRecordStepActive: boolean = false;
    public quizStepActive: boolean = false;

    public reading: boolean = false;
    public questionInProgress: boolean = false;
    public questionTAIs: any;
    public questionQuiz: any;
    public fluencyBars = Array(3).fill({});
    public TAI: string = "TAI";
    public QUIZ: string = "Quiz";
    private actionSessionProcessing: boolean = false;

    public get quizButtonLabel(): string {
        return this.quizService.quizButtonLabel;
    }

    constructor(public quizService: QuizService,
                private eventBus: EventBus,
                private renderer: Renderer2,
                private audioQueueService: AudioQueueService,
                public questionService: QuestionService,
                public userModelService: UserModelService,
                public stepService: StepService,
                public stepStateUpdater: StepStateUpdater,
                public userProgress: UserProgress,
                public sessionService: SessionService,
                public assignmentService: AssignmentService,
                private scrollService: ScrollService,
                public glossaryService: GlossaryService,
                private changeDetectorRef: ChangeDetectorRef,

                ) {
        super("StepComponent");
        this.updateStepAllowed();

        this.listenForSettingUpSteps();
        this.listenForStepStatusChange();
        this.listenForStepAllowedChange();
        this.listenForCountdownEvents();
        this.listenForTAIChange();

        this.updateStepOnSelectionChange();
        this.updateStepBlinking();
        this.stepService.changeDetectorRef = changeDetectorRef;
    }

    private listenForSettingUpSteps(): void {
        let previewSub = this.eventBus.pipe(filter((e: RaEvent) => e.type === PreviewStepEvent.TYPE))
            .subscribe((e: PreviewStepEvent) => {
                this.setupPreviewStep();
            });

        let fluencySub = this.eventBus.pipe(filter((e: RaEvent) => e.type === ReadAndRecordStepEvent.TYPE))
            .subscribe((e: ReadAndRecordStepEvent) => {
                this.setupFluencyStep();
            });

        let quizSub = this.eventBus.pipe(filter((e: RaEvent) => e.type === QuizStepEvent.TYPE))
            .subscribe((e: QuizStepEvent) => {
            this.setupQuizStep();
        });

        this.subscriptions.push(previewSub);
        this.subscriptions.push(fluencySub);
        this.subscriptions.push(quizSub);
    }

    private listenForStepStatusChange(): void {
        let statusSub = this.eventBus.pipe(filter((e: RaEvent) => e.type === StepStatusEvent.TYPE))
            .subscribe((e: StepStatusEvent) => {
            switch (e.getKey()) {
                case PreviewStepEvent.TYPE:
                    this.previewProgressValue = e.getValue();
                    break;
                case ReadAndRecordStepEvent.TYPE:
                    this.readAndRecordProgressValue = e.getValue();
                    this.parseFluencyProgress();
                    break;
                case QuizStepEvent.TYPE:
                    this.quizProgressValue = e.getValue();
                    break;
                default:
            }
        });
        this.subscriptions.push(statusSub);
    }

    private listenForStepAllowedChange(): void {
        let stepAllowedSub = this.eventBus.pipe(filter((e: RaEvent) => e.type === StepAllowedEvent.TYPE))
            .subscribe((e: StepAllowedEvent) => {
            switch (e.getKey()) {
                case PreviewStepEvent.TYPE:
                    this.previewStepAllowed = true;
                    break;
                case ReadAndRecordStepEvent.TYPE:
                    this.readAndRecordStepAllowed = true;
                    break;
                case QuizStepEvent.TYPE:
                    this.quizStepAllowed = true;
                    break;
                default:
            }
        });
        this.subscriptions.push(stepAllowedSub);
    }

    private listenForCountdownEvents(): void {
        let countdownStart = this.eventBus.pipe(filter((e: RaEvent) => e.type === CountdownStartEvent.TYPE))
            .subscribe((e: CountdownStartEvent) => {
            this.stepService.isCountdownOn = true;
        });

        let countdownStop = this.eventBus.pipe(filter((e: RaEvent) => e.type === CountdownStopEvent.TYPE))
            .subscribe((e: CountdownStopEvent) => {
            this.stepService.isCountdownOn = false;
        });

        this.subscriptions.push(countdownStart);
        this.subscriptions.push(countdownStop);
    }

    private listenForTAIChange(): void {
        let taiStart = this.eventBus.pipe(filter(e => e instanceof TaiEvent)).subscribe((e: TaiEvent) => {
            this.questionInProgress = e.status === TAI_STATUS.begin_tai;
        });

        this.subscriptions.push(taiStart);
    }

    private updateStepOnSelectionChange(): void {
        this.eventBus.pipe(filter(e => e instanceof SelectionLoadedEvent)).subscribe((e: SelectionLoadedEvent) => {
            if (this.stepService.current().getType() === BookSessionType.PREVIEW) {
                this.setupPreviewStep();
            } else if (this.stepService.current().getType() === BookSessionType.FLUENCY) {
                this.setupFluencyStep();
            } else if (this.stepService.current().getType() === BookSessionType.QUIZ) {
                this.setupQuizStep();
            }
        });
    }

    private updateStepBlinking(): void {

        this.renderer.listen("document", "click", (event) => {
            if (this.stepService.isBlinking()) {
                this.stepService.setAutomaticMove(false);
                this.userProgress.setTransitionActive(false);
                this.turnOffBlinking();
            }
        });
    }

    private updateStepAllowed(): void {
        this.previewStepAllowed = true;
        this.readAndRecordStepAllowed = this.stepService.isStepAccessible(new ReadAndRecordStep());
        this.quizStepAllowed = this.stepService.isStepAccessible(new QuizStep());
    }

    private resetActiveClasses(): void {
        this.previewStepActive = false;
        this.readAndRecordStepActive = false;
        this.quizStepActive = false;
        this.stepService.setAutomaticMove(false);
        this.userProgress.setTransitionActive(false);
        this.updateStepAllowed();
    }

    private setupPreviewStep(): void {
        this.resetActiveClasses();
        this.previewStepActive = true;
    }

    private setupFluencyStep(): void {
        this.resetActiveClasses();
        this.readAndRecordStepActive = true;
        this.parseFluencyProgress();
    }

    private setupQuizStep(): void {
        this.resetActiveClasses();
        this.quizStepActive = true;
    }

    public startedReadOrRecordActivity(): boolean {
        return this.stepService.startedReadOrRecordActivity;
    }

    private determineProgressActiveState(question: IQuestionEntry, questionType: string): QuestionState {
        if (questionType === this.TAI) {
            return this.userModelService.findThinkAboutQuestionState(
                Number(this.questionService.retrieveQuestionIndex(question)));
        } else if (questionType === this.QUIZ) {
            return this.userModelService.findQuizQuestionState(
                Number(this.questionService.retrieveQuestionIndex(question)));
        }
    }

    private determineProgressBadge(value: number, hasStar: boolean,
                                   stepComplete: boolean, hasSilverStar?:boolean): any {
        return {
            "step-start": value === 0,
            "step-progress": !stepComplete && value > 0 && value <= 100,
            "step-checkmark": stepComplete && value >= 100 && !hasStar && !hasSilverStar,
            "step-goldstar": stepComplete && value >= 100 && hasStar && !hasSilverStar,
            "step-silverstar": stepComplete && !!hasSilverStar
        };
    }

    public showStepOneVerticalSelectors(): {"col-sm-5": boolean, "col-sm-1": boolean,
        "col-sm-2": boolean, "minimum": boolean, "completed": boolean} {
        return {
            "col-sm-5": this.previewStepActive,
            "col-sm-1": window.innerWidth <= 1120 && this.readAndRecordStepActive,
            "col-sm-2": (this.quizStepActive || window.innerWidth > 1120) && !this.previewStepActive,
            "minimum": !this.previewStepActive,
            "completed":this.userModelService.taqCompletionState === CompletionState.COMPLETED
        };
    }

    public goToPreviewStep(): void {
        if (this.startedReadOrRecordActivity()) {
            return;
        }

        this.audioQueueService.hardReset();

        if (this.previewStepAllowed) {
            this.setupPreviewStep();
            this.stepService.requestStep(new PreviewStep());
            this.scrollService.scrollText();
        }
    }

    public goToReadAndRecordStep(): void {
        if (this.startedReadOrRecordActivity()) {
            return;
        }

        this.audioQueueService.hardReset();

        if (this.readAndRecordStepAllowed) {
            this.setupFluencyStep();
            this.stepService.requestStep(new ReadAndRecordStep());
        } else {
            this.eventBus.next(new AudioQueueEvent([
                new AudioQueueItem("RA_STEP_1_03", TranslateAudio.YES)], InterruptAudio.YES));
        }
    }

    public goToQuizStep(): void {
        if (this.startedReadOrRecordActivity()) {
            return;
        }

        this.audioQueueService.hardReset();

        if (this.quizStepAllowed) {
            this.resetActiveClasses();
            this.quizStepActive = true;
            this.stepService.requestStep(new QuizStep());
            this.scrollService.scrollText();
        } else if (this.stepService.isPreview) {
            this.eventBus.next(new AudioQueueEvent([
                new AudioQueueItem("RA_STEP_1_03", TranslateAudio.YES)], InterruptAudio.YES));
        } else if (this.stepService.isFluency) {
            this.eventBus.next(new AudioQueueEvent([
                new AudioQueueItem("RA_STEP_2_04", TranslateAudio.YES)], InterruptAudio.YES));
        }
    }

    public startQuiz(): void {
        // prevent double clicking of the button which triggers duplicated bookSession calls
        if (!this.actionSessionProcessing) {
            this.actionSessionProcessing = true;
            this.audioQueueService.reset().subscribe(() => {
                this.sessionService.startBookSession(this.assignmentService.currentSelection, BookSessionType.QUIZ
                ).subscribe(() => {
                    this.actionSessionProcessing = false;
                    this.eventBus.next(new StartQuizEvent());
                });
            });
        }
    }

    public recordSelection(): void {
        // prevent double clicking of the button which triggers duplicated bookSession calls
        if (!this.actionSessionProcessing) {
            this.actionSessionProcessing = true;
            this.audioQueueService.reset().subscribe(() => {
                this.sessionService.startBookSession(this.assignmentService.currentSelection, BookSessionType.FLUENCY
                ).subscribe(() => {
                    this.actionSessionProcessing = false;
                    this.eventBus.next(new RecordSelectionEvent());
                });
            });
        }
    }

    public pauseRecording(): void {
        if (!this.stepService.isCountdownOn) {
            this.audioQueueService.hardReset();
            this.eventBus.next(new PauseSelectionEvent());
        }
    }

    public readSelection(): void {
        this.audioQueueService.reset().subscribe(() => {
            this.eventBus.next(new ReadSelectionEvent());
        });
    }

    public pauseReading(): void {
        this.audioQueueService.reset().subscribe(() => {
            this.eventBus.next(new EnableExitButtonEvent());
            this.eventBus.next(new PauseSelectionEvent());
        });
    }

    public goNextSentence(): void {
        this.eventBus.next(new NextSentenceEvent());
    }

    public microphoneCheck(): void {
        this.eventBus.next(new MicrophoneCheckEvent(MICROPHONECHECK_STATUS.manual));
    }

    public isReading(): boolean {
        return this.stepService.isReading;
    }

    public isReadingMyRecording(): boolean {
        return this.stepService.isReadingMyRecording;
    }

    public isRecording(): boolean {
        return this.stepService.isRecording;
    }

    public showRecordButton(): boolean {
        return this.stepService.isRecording || this.actionSessionProcessing;
    }

    public isVerticalDirection(): boolean {
        return this.direction === "vertical";
    }

    public isHorizontalDirection(): boolean {
        return this.direction === "horizontal";
    }

    public readMyRecording(): void {
        this.audioQueueService.reset().subscribe(() => {
            this.eventBus.next(new ReadMyRecordingEvent());
        });
    }

    public parseQuestionStates(questionEntries: IQuestionEntry[], questionType?: string): boolean[] {
        let states = [];
        for (let questionEntry of questionEntries) {
            let activeState = this.determineProgressActiveState(questionEntry, questionType);
            let answeredCorrectly = activeState === QuestionState.ANSWERED_CORRECTLY ||
                                    activeState === QuestionState.PREVIOUSLY_ANSWERED_CORRECTLY;
            let answeredIncorrectly = activeState === QuestionState.ANSWERED_INCORRECTLY ||
                                      activeState === QuestionState.PREVIOUSLY_ANSWERED_INCORRECTLY;
            let activeValid = answeredCorrectly || answeredIncorrectly;

            if (activeValid) {
                states.unshift(activeValid);
            } else {
                states.push(activeValid);
            }
        }
        return states;
    }

    public parseFluencyProgress(): void {
        let maxShownRecording = 3;
        let recordCount = this.stepStateUpdater.recordCountProgress();
        this.fluencyBars = [];

        for (let i = 0; i < maxShownRecording; i++) {
            if (recordCount >= maxShownRecording) {
                this.fluencyBars.push({ "completed": true });
            } else if (recordCount > i) {
                this.fluencyBars.push({ "partial": true });
            } else {
                this.fluencyBars.push({});
            }
        }
    }

    public getCurrentProgressBadge(stepType: string): any {
        let stepCompletionState;

        if (stepType === "preview") {
            stepCompletionState = this.stepStateUpdater.isStepCompleted(this.userModelService.taqCompletionState);
            return this.determineProgressBadge(this.previewProgressValue,
                                               this.userModelService.hasTAQStar, stepCompletionState);
        } else if (stepType === "fluency") {
            stepCompletionState = this.stepStateUpdater.isStepCompleted(this.userModelService.recordCompletionState);
            return this.determineProgressBadge(this.readAndRecordProgressValue,
                                               this.userModelService.hasRecordingStar, stepCompletionState,
                                               this.userProgress.hasSilverRecordingStar());
        } else if (stepType === "quiz") {
            stepCompletionState = this.stepStateUpdater.isStepCompleted(this.userModelService.quizCompletionState);
            return this.determineProgressBadge(this.quizProgressValue, this.userModelService.hasQuizStar,
                                               stepCompletionState);
        }
    }

    public isStepComplete(stepType: string): boolean {

        if (stepType === "preview") {
            return this.stepStateUpdater.isStepCompleted(this.userModelService.taqCompletionState);
        } else if (stepType === "fluency") {
            return this.stepStateUpdater.isStepCompleted(this.userModelService.recordCompletionState);
        } else if (stepType === "quiz") {
            return this.stepStateUpdater.isStepCompleted(this.userModelService.quizCompletionState);
        }
    }

    public turnOffBlinking(): void {
        this.stepService.setStepTwoBlinking(false);
        this.stepService.setStepThreeBlinking(false);
        this.userProgress.setTransitionActive(false);
    }

    public passMiniVersionRule(stepType: string): any {
        let badgeMap = this.getCurrentProgressBadge(stepType);
        return badgeMap["step-checkmark"] || badgeMap["step-goldstar"] || badgeMap["step-silverstar"];
    }

    public getQuestionTAIs(): IQuestionClass[] {
        let foo:IQuestionClass = {"regular-progress-icon": true, "active": false};
        return [foo];
    }

    public getQuestionClass(index: number): IQuestionClass {
        return {"regular-progress-icon": true, "active": false};
    }

    public getTAIStates(): boolean[] {
        return this.parseQuestionStates(this.questionService.retrieveTAQs(), this.TAI);
    }

    public getQuizStates(): boolean[] {
        return this.parseQuestionStates(this.questionService.retrieveQuiz(), this.QUIZ);
    }

    public iconForTAIIsRegularSize(): boolean {
        // return [true, false];
        if (this.questionService.retrieveTAQs().length > 5) {
            return false;
        }
        return true;
    }

    public iconForQuizIsRegularSize(): boolean {
        // return [true, false];
        if (this.questionService.retrieveQuiz().length > 5) {
            return false;
        }
        return true;
    }

    public readToMeDisabled(): boolean {
        return this.isRecording() || this.stepService.isCountdownOn || this.isReadingMyRecording()
            || this.userProgress.isTransitionActive();
    }

    public playMyReadingDisabled(): boolean {
        let startedRecordingAgain = this.userModelService.currentSentenceId > 0;
        // even if the user has recordings, they may be deleted due to space constraints.  canPlayback false reflects this state
        let noRecordings = this.userModelService.recordCount === 0 || !this.userModelService.canPlayback;
        return this.isRecording() || this.stepService.isCountdownOn || noRecordings || startedRecordingAgain
            || this.isReading() || this.userProgress.isTransitionActive();
    }

    public isStepOneCircleDisabled(showCountdown: boolean): boolean {
        let isNotStepOneReading = this.isReading() && !this.previewStepActive;
        return isNotStepOneReading || this.isRecording() || this.isReadingMyRecording() || showCountdown;
    }

    public isStepTwoCircleDisabled(showCountdown: boolean): boolean {
        return this.isReading() && !this.readAndRecordStepActive;
    }

    public quizBtnDisabled(): boolean {
        return this.userProgress.isTransitionActive();
    }

    public recordBtnDisabled(): boolean {
        return this.userProgress.isTransitionActive();
    }

}
